import * as React from 'react';
import { arrow, autoUpdate, offset, flip, shift, useFloating, useClick, useDismiss, useRole, useInteractions, FloatingPortal, FloatingFocusManager, useTransitionStatus, useHover, safePolygon, useId, useMergeRefs, } from '@floating-ui/react';
import clsx from 'clsx';
import { useIsMounted } from '@core-front/helper';
import { InputContext } from '@core-front/input-provider';
import { useLabels } from '@core-front/label-provider';
import PropsChecker from '@core-front/props-checker';
import { useTheme } from '@core-front/theme-provider';
const TooltipCore = (props) => {
    return (React.createElement(PropsChecker, { componentProps: props, componentName: "Tooltip", defaultValueName: "defaultIsShown", valueName: "isShown", onChangeNames: "onToggle", onChangeAltNames: ['onShow', 'onHide'] },
        React.createElement(TooltipInner, Object.assign({}, props))));
};
export default TooltipCore;
const TooltipInner = (props) => {
    var _a, _b, _c, _d;
    const labels = useLabels();
    const { input: propInputIntent } = React.useContext(InputContext);
    const { portalRoot: portalContainer } = useTheme();
    const { 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy, children, components: { CloseButton }, className, fixedWidth = true, noPadding, size, onHeader, isCloseButtonVisible = true, trigger, referenceElement, elementRef, } = props;
    const [referenceId, setReferenceId] = React.useState(null);
    const internalReferenceId = useId();
    const inputIntent = React.useMemo(() => {
        if (trigger === 'click') {
            return 'touch';
        }
        return propInputIntent;
    }, [trigger, propInputIntent]);
    const state = useTooltip(Object.assign(Object.assign({}, props), { inputIntent }));
    const { setOpen } = state;
    const isMounted = useIsMounted();
    const ref = useMergeRefs([state.refs.setFloating, elementRef]);
    const handleCloseClick = React.useCallback((event) => {
        event.preventDefault();
        setOpen(false);
    }, [setOpen]);
    const isCloseButtonShown = React.useMemo(() => {
        return !!(isCloseButtonVisible && ['initial', 'pointer', 'touch', 'keyboard'].indexOf(inputIntent) > -1);
    }, [isCloseButtonVisible, inputIntent]);
    const labelledBy = (_a = ariaLabelledBy !== null && ariaLabelledBy !== void 0 ? ariaLabelledBy : referenceId) !== null && _a !== void 0 ? _a : internalReferenceId;
    React.useEffect(() => {
        if (React.isValidElement(referenceElement)) {
            setReferenceId(referenceElement.props.id);
        }
    }, [referenceElement]);
    const content = (React.createElement("div", Object.assign({ ref: ref, style: state.floatingStyles, className: clsx('tooltip', `tooltip--trigger-${inputIntent}`, `tooltip--placement-${state.placement}`, `is-${state.status}`, {
            ['tooltip--has-close-button']: isCloseButtonShown,
            ['tooltip--fixed-width']: !!fixedWidth,
            ['tooltip--no-padding']: !!noPadding,
            [`tooltip--${size}`]: !!size,
            ['tooltip--on-header']: !!onHeader,
        }, className), tabIndex: -1, role: "dialog", "aria-modal": true, "aria-labelledby": labelledBy, "aria-describedby": ariaDescribedBy }, state.getFloatingProps({
        // Prevent click events from propagating to portal parents
        onClick: e => e.stopPropagation(),
    })),
        React.createElement("div", { className: "tooltip__inner" },
            React.createElement("div", { className: "tooltip__arrow", ref: state.arrowRef, style: {
                    position: 'absolute',
                    left: (_b = state.middlewareData.arrow) === null || _b === void 0 ? void 0 : _b.x,
                    top: (_c = state.middlewareData.arrow) === null || _c === void 0 ? void 0 : _c.y,
                } }),
            React.createElement("div", { className: "tooltip__content" },
                isCloseButtonShown && React.createElement(CloseButton, { className: "tooltip__close", onClick: handleCloseClick }),
                children))));
    // To render Tooltip in SSR, plus so it would attach parent as reference correctly.
    if (!isMounted) {
        return (React.createElement(React.Fragment, null,
            React.isValidElement(referenceElement) ? referenceElement : null,
            content));
    }
    return (React.createElement(React.Fragment, null,
        React.isValidElement(referenceElement)
            ? React.cloneElement(referenceElement, state.getReferenceProps({
                ref: state.refs.setReference,
                id: (_d = referenceElement.props.id) !== null && _d !== void 0 ? _d : internalReferenceId,
            }))
            : null,
        state.isMounted && (React.createElement(FloatingPortal, { root: portalContainer },
            React.createElement(FloatingFocusManager, { context: state.context, modal: true, initialFocus: state.reason === 'hover' ? -1 : 0, returnFocus: true, visuallyHiddenDismiss: isCloseButtonShown ? false : labels.get('close') }, content)))));
};
function useTooltip({ defaultIsShown = false, placement = 'top', isShown: controlledIsShown, onToggle, onHide, onShow, referenceElement, trigger = 'auto', outsideClickCloseFilter, hideWithIntent, inputIntent, }) {
    const parentRef = React.useRef(null);
    const arrowRef = React.useRef(null);
    const [reason, setReason] = React.useState(undefined);
    const [uncontrolledIsShown, setUncontrolledIsShown] = React.useState(defaultIsShown);
    const open = React.useMemo(() => {
        return (onToggle || (onHide && onShow)) && typeof controlledIsShown !== 'undefined' ? controlledIsShown : uncontrolledIsShown;
    }, [onToggle, onHide, onShow, controlledIsShown, uncontrolledIsShown]);
    const handleOpenChange = React.useCallback((nextOpen, _e, changeReason) => {
        // With React 17, "safe-polygon" as change reason trips focus management up (scrolls page to top on unhover). In practice it is hover event, so we can normalize it.
        const normalizedChangeReason = changeReason === 'safe-polygon' ? 'hover' : changeReason;
        setReason(normalizedChangeReason);
        if (typeof controlledIsShown === 'undefined') {
            setUncontrolledIsShown(nextOpen);
        }
        if (nextOpen !== open) {
            if (onHide && nextOpen === false) {
                onHide();
            }
            if (onShow && nextOpen === true) {
                onShow();
            }
            if (onToggle) {
                onToggle(nextOpen);
            }
        }
    }, [onToggle, onHide, onShow, controlledIsShown, open]);
    const toggle = React.useCallback(() => {
        handleOpenChange(!open);
    }, [handleOpenChange, open]);
    const data = useFloating({
        placement,
        open,
        onOpenChange: handleOpenChange,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(18),
            flip({
                crossAxis: placement.includes('-'),
                fallbackAxisSideDirection: 'end',
                padding: { top: 137, right: 17, bottom: 17, left: 17 },
            }),
            shift({ padding: 15 }),
            arrow({
                element: arrowRef,
                padding: 8,
            }),
        ],
    });
    const { context, refs } = data;
    React.useEffect(() => {
        let triggerRef = null;
        // If referenceElement is a React element, then ref and event listeners are set by spreading props in main Tooltip render.
        if (React.isValidElement(referenceElement)) {
            return;
        }
        if (typeof referenceElement === 'string') {
            triggerRef = document.querySelector(referenceElement);
        }
        else if (referenceElement instanceof SVGElement || referenceElement instanceof HTMLElement) {
            triggerRef = referenceElement;
        }
        else if (refs.floating.current && !parentRef.current) {
            triggerRef = refs.floating.current.parentElement;
            parentRef.current = refs.floating.current.parentElement;
        }
        if (trigger !== 'none') {
            if (parentRef.current) {
                parentRef.current.addEventListener('click', toggle);
            }
            else if (triggerRef) {
                triggerRef.addEventListener('click', toggle);
            }
        }
        if (triggerRef) {
            refs.setReference(triggerRef);
        }
        return () => {
            if (trigger !== 'none') {
                if (parentRef.current) {
                    parentRef.current.removeEventListener('click', toggle);
                }
                else if (triggerRef) {
                    triggerRef.removeEventListener('click', toggle);
                }
            }
        };
    }, [referenceElement, refs, toggle, trigger]);
    const hover = useHover(context, {
        handleClose: safePolygon(),
        enabled: trigger === 'auto' && !(hideWithIntent === null || hideWithIntent === void 0 ? void 0 : hideWithIntent.includes(inputIntent)),
    });
    const click = useClick(context, {
        enabled: trigger !== 'none' && !(hideWithIntent === null || hideWithIntent === void 0 ? void 0 : hideWithIntent.includes(inputIntent)),
    });
    const dismiss = useDismiss(context, {
        outsidePress: event => {
            let target = event.target;
            while (target !== null) {
                if (target instanceof Element) {
                    if (outsideClickCloseFilter && outsideClickCloseFilter(target)) {
                        return false;
                    }
                    target = target.parentElement;
                }
            }
            return true;
        },
    });
    const role = useRole(context, {
        role: 'dialog',
    });
    const interactions = useInteractions([hover, click, dismiss, role]);
    const transitionStatus = useTransitionStatus(context, { duration: 100 });
    return React.useMemo(() => (Object.assign(Object.assign(Object.assign({ open, setOpen: handleOpenChange, arrowRef,
        reason }, interactions), data), transitionStatus)), [open, handleOpenChange, interactions, data, transitionStatus, reason]);
}
